import { useEffect, useRef, useState } from "react";
import { Plus, FileSignature, Send, ShieldCheck, X, Ban, FileText, Trash2 } from "lucide-react";
import api, { API_BASE } from "@/lib/api";
import { PageHeader, StatusChip, EmptyState } from "@/components/Common";
import { toast } from "sonner";

export default function RemissionGuides() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/remission-guides").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/invoices").then((r) => setInvoices(r.data.filter((i) => i.status === "autorizada")));
  }, []);

  const action = async (id, path) => {
    try { await api.post(`/remission-guides/${id}/${path}`); toast.success(`Operación '${path}' completada.`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div data-testid="remission-guides-page">
      <PageHeader
        title="Guías de Remisión"
        subtitle="Documento para sustentar el traslado físico de la mercadería (SRI cod 06)."
        testid="remission-guides-header"
        action={<button data-testid="remission-guides-create-button" onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2"><Plus className="w-4 h-4" /> Nueva guía</button>}
      />

      {showForm && <GuideForm
        customers={customers} products={products} invoices={invoices}
        onClose={() => setShowForm(false)} onCreated={() => { setShowForm(false); load(); }}
      />}

      {items.length === 0 ? (
        <EmptyState title="Sin guías de remisión" hint="Emite una guía cada vez que entregues mercadería." testid="remission-guides-empty" />
      ) : (
        <div className="card-soft overflow-x-auto">
          <table className="w-full text-sm" data-testid="remission-guides-table">
            <thead><tr className="text-left text-[11px] uppercase tracking-[0.18em] text-[#8A9A92]">
              <th className="px-5 py-3">Número</th><th className="px-5 py-3">Destinatario</th>
              <th className="px-5 py-3">Transportista</th><th className="px-5 py-3">Transporte</th>
              <th className="px-5 py-3">Estado</th><th className="px-5 py-3 text-right">Acciones</th>
            </tr></thead>
            <tbody>
              {items.map((g) => (
                <tr key={g.id} className="border-t border-[#E2E0D8] hover:bg-[#FAFAF8]">
                  <td className="px-5 py-3 font-mono">{g.document_number}</td>
                  <td className="px-5 py-3">{g.customer_name}</td>
                  <td className="px-5 py-3 text-xs"><div>{g.transporter_name}</div><div className="font-mono text-[#8A9A92]">{g.transporter_license_plate || "—"}</div></td>
                  <td className="px-5 py-3 text-xs text-[#5C6B64]">{new Date(g.fecha_inicio_transporte).toLocaleDateString()} → {new Date(g.fecha_fin_transporte).toLocaleDateString()}</td>
                  <td className="px-5 py-3"><StatusChip status={g.status} /></td>
                  <td className="px-5 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <a title="PDF" href={`${API_BASE}/remission-guides/${g.id}/pdf`} target="_blank" rel="noreferrer" className="p-2 rounded hover:bg-[#F3F2ED] inline-flex"><FileText className="w-4 h-4 text-[#5C6B64]" /></a>
                      {g.status === "borrador" && <button title="Firmar" onClick={() => action(g.id, "sign")} className="p-2 rounded hover:bg-[#DDE6ED]"><FileSignature className="w-4 h-4 text-[#608090]" /></button>}
                      {(g.status === "firmada" || g.status === "rechazada") && <button title="Enviar" onClick={() => action(g.id, "send")} className="p-2 rounded hover:bg-[#DDE6ED]"><Send className="w-4 h-4 text-[#608090]" /></button>}
                      {["firmada","enviada","rechazada"].includes(g.status) && <button title="Autorizar" onClick={() => action(g.id, "authorize")} className="p-2 rounded hover:bg-[#E4EFE6]"><ShieldCheck className="w-4 h-4 text-[#507A59]" /></button>}
                      {g.status !== "anulada" && <button title="Anular" onClick={() => { if (window.confirm("¿Anular guía?")) action(g.id, "cancel"); }} className="p-2 rounded hover:bg-[#F3DADA]"><Ban className="w-4 h-4 text-[#A44A4A]" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const todayPlus = (d) => { const x = new Date(); x.setDate(x.getDate() + d); return x.toISOString().slice(0, 10); };

function GuideForm({ customers, products, invoices, onClose, onCreated }) {
  const [form, setForm] = useState({
    transporter_identification: "", transporter_identification_type: "04", transporter_name: "", transporter_license_plate: "",
    fecha_inicio_transporte: todayPlus(0), fecha_fin_transporte: todayPlus(1),
    direccion_partida: "",
    customer_id: "", direccion_destinatario: "", motivo_traslado: "Venta",
    invoice_id: "",
  });
  const uidRef = useRef(0);
  const newRow = () => ({ uid: ++uidRef.current, product_id: "", quantity: 1 });
  const [items, setItems] = useState(() => [newRow()]);
  const [saving, setSaving] = useState(false);

  const setF = (k, v) => setForm({ ...form, [k]: v });
  const setItem = (i, patch) => { const n = items.slice(); n[i] = { ...n[i], ...patch }; setItems(n); };
  const addRow = () => setItems([...items, newRow()]);
  const rmRow = (i) => setItems(items.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (items.some((i) => !i.product_id)) return toast.error("Selecciona los productos");
    setSaving(true);
    try {
      await api.post("/remission-guides", {
        ...form,
        fecha_inicio_transporte: new Date(form.fecha_inicio_transporte).toISOString(),
        fecha_fin_transporte: new Date(form.fecha_fin_transporte).toISOString(),
        invoice_id: form.invoice_id || null,
        items: items.map((it) => ({ product_id: it.product_id, quantity: Number(it.quantity) })),
      });
      toast.success("Guía creada");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid="guide-form" className="card-soft p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-xl font-bold">Nueva guía de remisión</h3>
        <button onClick={onClose} className="text-[#8A9A92] hover:text-[#2A3F35]"><X className="w-5 h-5" /></button>
      </div>
      <form onSubmit={submit} className="space-y-5">
        <section>
          <h4 className="font-heading font-bold mb-3 text-[#2A3F35]">Transportista</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Razón social *</span>
              <input data-testid="guide-transporter-name" required className="input-soft" value={form.transporter_name} onChange={(e) => setF("transporter_name", e.target.value)} />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Tipo ID *</span>
              <select className="input-soft" value={form.transporter_identification_type} onChange={(e) => setF("transporter_identification_type", e.target.value)}>
                <option value="04">RUC</option><option value="05">Cédula</option><option value="06">Pasaporte</option>
              </select>
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">RUC / Cédula *</span>
              <input data-testid="guide-transporter-id" required className="input-soft" value={form.transporter_identification} onChange={(e) => setF("transporter_identification", e.target.value)} />
            </label>
            <label className="block md:col-span-2">
              <span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Placa vehículo</span>
              <input className="input-soft" placeholder="ABC-1234" value={form.transporter_license_plate} onChange={(e) => setF("transporter_license_plate", e.target.value)} />
            </label>
          </div>
        </section>

        <section>
          <h4 className="font-heading font-bold mb-3 text-[#2A3F35]">Transporte</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Fecha inicio *</span><input required type="date" className="input-soft" value={form.fecha_inicio_transporte} onChange={(e) => setF("fecha_inicio_transporte", e.target.value)} /></label>
            <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Fecha fin *</span><input required type="date" className="input-soft" value={form.fecha_fin_transporte} onChange={(e) => setF("fecha_fin_transporte", e.target.value)} /></label>
            <label className="block md:col-span-2"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Dirección de partida *</span><input required className="input-soft" value={form.direccion_partida} onChange={(e) => setF("direccion_partida", e.target.value)} /></label>
          </div>
        </section>

        <section>
          <h4 className="font-heading font-bold mb-3 text-[#2A3F35]">Destinatario</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Cliente *</span>
              <select data-testid="guide-customer-select" required className="input-soft" value={form.customer_id} onChange={(e) => setF("customer_id", e.target.value)}>
                <option value="">Seleccione</option>
                {customers.map((c) => <option key={c.id} value={c.id}>{c.name} ({c.identification})</option>)}
              </select>
            </label>
            <label className="block"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Factura asociada (opcional)</span>
              <select className="input-soft" value={form.invoice_id} onChange={(e) => setF("invoice_id", e.target.value)}>
                <option value="">— Ninguna —</option>
                {invoices.map((i) => <option key={i.id} value={i.id}>{i.document_number}</option>)}
              </select>
            </label>
            <label className="block md:col-span-2"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Dirección destinatario *</span><input required className="input-soft" value={form.direccion_destinatario} onChange={(e) => setF("direccion_destinatario", e.target.value)} /></label>
            <label className="block md:col-span-2"><span className="text-xs uppercase tracking-[0.18em] text-[#8A9A92] mb-1.5 block">Motivo del traslado *</span><input required className="input-soft" value={form.motivo_traslado} onChange={(e) => setF("motivo_traslado", e.target.value)} /></label>
          </div>
        </section>

        <section>
          <h4 className="font-heading font-bold mb-3 text-[#2A3F35]">Productos</h4>
          <div className="border border-[#E2E0D8] rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 gap-2 p-3 bg-[#F3F2ED] text-[11px] uppercase tracking-[0.16em] text-[#5C6B64]">
              <div className="col-span-8">Producto</div>
              <div className="col-span-3">Cantidad</div>
              <div className="col-span-1"></div>
            </div>
            {items.map((it, idx) => (
              <div key={it.uid} className="grid grid-cols-12 gap-2 p-3 border-t border-[#E2E0D8] items-center">
                <select required className="input-soft col-span-8" value={it.product_id} onChange={(e) => setItem(idx, { product_id: e.target.value })}>
                  <option value="">Seleccione</option>
                  {products.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>)}
                </select>
                <input type="number" step="0.01" min="0" required className="input-soft col-span-3" value={it.quantity} onChange={(e) => setItem(idx, { quantity: e.target.value })} />
                <div className="col-span-1 flex justify-end">
                  {items.length > 1 && <button type="button" onClick={() => rmRow(idx)} className="p-2 rounded hover:bg-[#F3DADA]"><Trash2 className="w-4 h-4 text-[#A44A4A]" /></button>}
                </div>
              </div>
            ))}
            <div className="p-3 border-t border-[#E2E0D8] bg-[#FAFAF8]">
              <button type="button" onClick={addRow} className="btn-secondary text-sm flex items-center gap-2"><Plus className="w-4 h-4" /> Agregar producto</button>
            </div>
          </div>
        </section>

        <div className="flex justify-end gap-3">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button data-testid="guide-save-button" disabled={saving} className="btn-primary" type="submit">{saving ? "Guardando..." : "Crear guía"}</button>
        </div>
      </form>
    </div>
  );
}
